import React, { useState } from 'react';
import { useListarFuncionarios, useCadastrarUsuario, useAtualizarUsuario, useAlternarStatusUsuario, useListarEstabelecimentos } from '../../hooks/useAdmin';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { TipoUsuario } from '../../types';
import Button from '../../components/Common/Button';

interface CreateFormData {
  nome: string;
  email: string;
  senha: string;
  tipo: TipoUsuario;
  estabelecimentoId: string;
}

interface EditFormData {
  id: number;
  nome: string;
  email: string;
}

const tipoLabels: Record<TipoUsuario, string> = {
  [TipoUsuario.Administrador]: 'Administrador',
  [TipoUsuario.GerenteEstabelecimento]: 'Gerente de Estabelecimento',
  [TipoUsuario.FuncionarioEstabelecimento]: 'Funcionário',
};

const Funcionarios: React.FC = () => {
  usePageTitle('Funcionários');
  const { user: usuarioLogado } = useAuth();
  const isAdmin = usuarioLogado?.tipo === TipoUsuario.Administrador;

  const [filtroNome, setFiltroNome] = useState('');
  const [filtroEmail, setFiltroEmail] = useState('');
  const [filtroEstabelecimentoId, setFiltroEstabelecimentoId] = useState('');

  const { data: funcionarios, isLoading } = useListarFuncionarios(
    isAdmin
      ? {
          nome: filtroNome || undefined,
          email: filtroEmail || undefined,
          estabelecimentoId: filtroEstabelecimentoId ? parseInt(filtroEstabelecimentoId) : undefined,
        }
      : undefined
  );

  const { data: estabelecimentos } = useListarEstabelecimentos();
  const { mutate: cadastrar, isPending: cadastrando } = useCadastrarUsuario();
  const { mutate: atualizar, isPending: atualizando } = useAtualizarUsuario();
  const { mutate: alternarStatus } = useAlternarStatusUsuario();

  const [mostrarModalCriar, setMostrarModalCriar] = useState(false);
  const [mostrarModalEditar, setMostrarModalEditar] = useState(false);

  const [createForm, setCreateForm] = useState<CreateFormData>({
    nome: '',
    email: '',
    senha: '',
    tipo: TipoUsuario.FuncionarioEstabelecimento,
    estabelecimentoId: '',
  });

  const [editForm, setEditForm] = useState<EditFormData>({ id: 0, nome: '', email: '' });

  const abrirModalCriar = () => {
    setCreateForm({
      nome: '',
      email: '',
      senha: '',
      tipo: TipoUsuario.FuncionarioEstabelecimento,
      estabelecimentoId: isAdmin ? '' : String(usuarioLogado?.estabelecimentoId ?? ''),
    });
    setMostrarModalCriar(true);
  };

  const abrirModalEditar = (f: { id: number; nome: string; email: string }) => {
    setEditForm({ id: f.id, nome: f.nome, email: f.email });
    setMostrarModalEditar(true);
  };

  const handleCriar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuarioLogado) return;

    const estabelecimentoId = isAdmin
      ? createForm.estabelecimentoId ? parseInt(createForm.estabelecimentoId) : null
      : (usuarioLogado.estabelecimentoId ?? null);

    cadastrar(
      {
        nome: createForm.nome,
        email: createForm.email,
        senha: createForm.senha,
        tipo: createForm.tipo,
        estabelecimentoId,
        usuarioLogadoId: usuarioLogado.id,
      },
      { onSuccess: () => setMostrarModalCriar(false) }
    );
  };

  const handleEditar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuarioLogado) return;

    atualizar(
      { id: editForm.id, nome: editForm.nome, email: editForm.email },
      { onSuccess: () => setMostrarModalEditar(false) }
    );
  };

  const tiposParaCriar = isAdmin
    ? [TipoUsuario.Administrador, TipoUsuario.GerenteEstabelecimento, TipoUsuario.FuncionarioEstabelecimento]
    : [TipoUsuario.FuncionarioEstabelecimento];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Funcionários</h1>
          <p className="text-gray-500 text-sm mt-1">
            {isAdmin ? 'Gerencie os funcionários de todos os estabelecimentos' : 'Gerencie os funcionários do seu estabelecimento'}
          </p>
        </div>
        <Button onClick={abrirModalCriar} variant="primary" size="md">
          Novo Funcionário
        </Button>
      </div>

      {isAdmin && (
        <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Filtrar por nome..."
            value={filtroNome}
            onChange={(e) => setFiltroNome(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 flex-1 min-w-48"
          />
          <input
            type="text"
            placeholder="Filtrar por email..."
            value={filtroEmail}
            onChange={(e) => setFiltroEmail(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 flex-1 min-w-48"
          />
          <select
            value={filtroEstabelecimentoId}
            onChange={(e) => setFiltroEstabelecimentoId(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 flex-1 min-w-48"
          >
            <option value="">Todos os estabelecimentos</option>
            {estabelecimentos?.map((est) => (
              <option key={est.id} value={est.id}>{est.nome}</option>
            ))}
          </select>
        </div>
      )}

      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">Carregando funcionários...</p>
        </div>
      ) : funcionarios && funcionarios.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                {isAdmin && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>}
                {isAdmin && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estabelecimento</th>}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {funcionarios.map((f) => {
                const nomeEstabelecimento = f.estabelecimentoId
                  ? estabelecimentos?.find((e) => e.id === f.estabelecimentoId)?.nome ?? `#${f.estabelecimentoId}`
                  : '-';

                return (
                  <tr key={f.id} className={!f.ativo ? 'bg-gray-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{f.nome}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{f.email}</td>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {tipoLabels[f.tipo as TipoUsuario] ?? f.tipo}
                      </td>
                    )}
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{nomeEstabelecimento}</td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        f.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {f.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                      {f.id !== usuarioLogado?.id && (
                        <>
                          <button
                            onClick={() => abrirModalEditar(f)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => alternarStatus(f.id)}
                            className={f.ativo ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                          >
                            {f.ativo ? 'Desativar' : 'Ativar'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">Nenhum funcionário encontrado</p>
        </div>
      )}

      {/* Modal Criar */}
      {mostrarModalCriar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Novo Funcionário</h2>
            <form onSubmit={handleCriar} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={createForm.nome}
                  onChange={(e) => setCreateForm({ ...createForm, nome: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                <input
                  type="password"
                  value={createForm.senha}
                  onChange={(e) => setCreateForm({ ...createForm, senha: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                />
              </div>
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={createForm.tipo}
                    onChange={(e) => setCreateForm({ ...createForm, tipo: e.target.value as TipoUsuario })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    {tiposParaCriar.map((tipo) => (
                      <option key={tipo} value={tipo}>{tipoLabels[tipo]}</option>
                    ))}
                  </select>
                </div>
              )}
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estabelecimento</label>
                  <select
                    value={createForm.estabelecimentoId}
                    onChange={(e) => setCreateForm({ ...createForm, estabelecimentoId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    required={createForm.tipo !== TipoUsuario.Administrador}
                  >
                    <option value="">Selecione...</option>
                    {estabelecimentos?.map((est) => (
                      <option key={est.id} value={est.id}>{est.nome}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button type="submit" variant="primary" size="md" fullWidth disabled={cadastrando}>
                  {cadastrando ? 'Salvando...' : 'Cadastrar'}
                </Button>
                <Button type="button" onClick={() => setMostrarModalCriar(false)} variant="secondary" size="md" fullWidth disabled={cadastrando}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {mostrarModalEditar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Editar Funcionário</h2>
            <form onSubmit={handleEditar} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={editForm.nome}
                  onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" variant="primary" size="md" fullWidth disabled={atualizando}>
                  {atualizando ? 'Salvando...' : 'Salvar'}
                </Button>
                <Button type="button" onClick={() => setMostrarModalEditar(false)} variant="secondary" size="md" fullWidth disabled={atualizando}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Funcionarios;
